import { Injectable, computed, inject, signal } from '@angular/core';

import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { getError } from 'src/app/api/services/api-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ExceptionLoggingService } from 'src/app/components/logging/exception-logging.service';
import {
  CompanySelectedSource,
  TrackerService,
} from 'src/app/components/logging/tracker.service';
import { ListErrorHandlerService } from 'src/app/components/scrolling-list/services/list-error-handler.service';
import {
  AddressUtilsService,
  NO_ADDRESS_ON_RECORD,
} from 'src/app/shared/services/address-utils.service';
import { CompanyTreeExpansionService } from './company-tree-expansion.service';
import {
  BranchState,
  BranchStatus,
  CompanyNode,
  CompanyTreeRow,
} from 'src/app/app-common/types/company-tree';

const BRANCH_PAGE_SIZE = 40;

type CompanyLike = {
  id?: string;
  name?: string;
  parentId?: string | null;
  street?: string | null;
  city?: string | null;
  province?: string | null;
};

@Injectable()
export class CompanyTreeService {
  private readonly companyApi = inject(CompanyApiService);
  private readonly companyState = inject(CompanyStateService);
  private readonly expansion = inject(CompanyTreeExpansionService);
  private readonly addressUtils = inject(AddressUtilsService);
  private readonly exceptionLogging = inject(ExceptionLoggingService);
  private readonly listErrorHandler = inject(ListErrorHandlerService);
  private readonly tracker = inject(TrackerService);

  private readonly nodes = signal(new Map<string, CompanyNode>());
  private readonly branches = signal(new Map<string, BranchState>());
  private readonly expanded = signal(new Set<string>());
  private readonly knownLeaves = signal(new Set<string>());
  private readonly selectedPath = signal<string[]>([]);
  private readonly userCompanyId = signal<string | null>(null);
  private readonly selectedCompanyId = signal<string | null>(null);
  private readonly initialStatusState = signal<BranchStatus>('idle');
  private readonly initialError = signal<string | null>(null);

  readonly initialStatus = this.initialStatusState.asReadonly();
  readonly initialErrorMessage = this.initialError.asReadonly();

  readonly hasNoSubcompanies = computed(() => {
    const rootId = this.userCompanyId();
    if (!rootId) {
      return false;
    }

    const branch = this.branches().get(rootId);
    return branch?.status === 'loaded' && branch.children.length === 0;
  });

  readonly rows = computed(() => this.projectRows());

  async initialize(): Promise<void> {
    this.reset();
    this.initialStatusState.set('loading');
    this.initialError.set(null);

    const userCompanyId = this.companyState.getUserCompanyId();
    const userCompany = this.companyState.getCopyOfUserCompany();
    if (!userCompanyId || !userCompany?.id) {
      this.initialStatusState.set('error');
      return;
    }

    this.userCompanyId.set(userCompanyId);
    this.cacheNode(this.toNode(userCompany, true));

    const selectedCompany = this.companyState.getCopyOfSelectedCompany();
    const selectedId = selectedCompany?.id ?? userCompanyId;
    this.selectedCompanyId.set(selectedId);

    const path = await this.resolveAncestorPath(selectedCompany, userCompanyId);
    this.selectedPath.set(path);

    const expanded = new Set<string>([
      ...this.expansion.read(userCompanyId),
      ...path,
      userCompanyId,
    ]);
    this.expanded.set(expanded);

    try {
      await this.loadBranch(userCompanyId);
    } catch (error) {
      this.initialError.set(
        this.branches().get(userCompanyId)?.errorMessage ??
          this.listErrorHandler.processErrorCode('Companies', 'load', error),
      );
      this.initialStatusState.set('error');
      return;
    }

    await this.loadVisibleExpandedBranches(userCompanyId);
    this.initialStatusState.set('loaded');
  }

  async toggle(id: string): Promise<void> {
    if (this.expanded().has(id)) {
      const next = new Set(this.expanded());
      next.delete(id);
      this.expanded.set(next);
      this.persistExpanded();
      return;
    }

    this.markExpanded(id);
    this.persistExpanded();

    try {
      await this.loadBranch(id);
    } catch {
      return;
    }
  }

  async loadMore(id: string): Promise<void> {
    try {
      await this.loadBranch(id, { append: true });
    } catch {
      return;
    }
  }

  async retry(id: string): Promise<void> {
    const existing = this.branches().get(id);
    if (existing?.status !== 'error') {
      return;
    }

    const append = existing.children.length > 0 && !!existing.cursor;
    try {
      await this.loadBranch(id, append ? { append: true } : undefined);
    } catch {
      return;
    }
  }

  async select(id: string, source: CompanySelectedSource): Promise<void> {
    const next = new Set(this.expanded());
    for (const ancestorId of this.ancestorsOf(id)) {
      next.delete(ancestorId);
      next.add(ancestorId);
    }
    this.expanded.set(next);
    this.persistExpanded();

    const node = this.nodes().get(id);
    const userCompanyId = this.userCompanyId();
    this.tracker.companySelectedEvent(
      id,
      node?.name ?? '',
      id === userCompanyId,
      source,
    );

    try {
      await this.companyState.switchCompany(id);
    } catch (error) {
      console.error('Failed to load selected company.', error);
    }
  }

  private reset(): void {
    this.nodes.set(new Map());
    this.branches.set(new Map());
    this.expanded.set(new Set());
    this.knownLeaves.set(new Set());
    this.selectedPath.set([]);
    this.userCompanyId.set(null);
    this.selectedCompanyId.set(null);
    this.initialStatusState.set('idle');
    this.initialError.set(null);
  }

  private async resolveAncestorPath(
    selected: CompanyLike | null | undefined,
    userCompanyId: string,
  ): Promise<string[]> {
    if (!selected?.id || selected.id === userCompanyId) {
      return [];
    }

    this.cacheNode(this.toNode(selected));

    const path: string[] = [];
    let current: CompanyLike = selected;

    while (current.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      try {
        const parent = await this.companyApi.getCompany(parentId);
        this.cacheNode(this.toNode(parent));
        path.unshift(parent.id);
        current = parent;
      } catch (error) {
        this.handleAncestorFailure(error, parentId, userCompanyId);
        return path;
      }
    }

    path.unshift(userCompanyId);
    return path;
  }

  private handleAncestorFailure(
    error: unknown,
    parentId: string,
    userCompanyId: string,
  ): void {
    console.error('Failed to load company.', error);

    if (this.isForbiddenOrNotFound(error)) {
      this.expansion.prune(userCompanyId, parentId);
      return;
    }

    this.exceptionLogging.handle(error);
  }

  private async loadVisibleExpandedBranches(rootId: string): Promise<void> {
    let levelIds = this.branches().get(rootId)?.children ?? [];

    while (levelIds.length > 0) {
      const expanded = this.expanded();
      const toLoad = levelIds.filter((id) => expanded.has(id));

      await Promise.all(
        toLoad.map(async (id) => {
          try {
            await this.loadBranch(id);
          } catch {
            return;
          }
        }),
      );

      const next: string[] = [];
      const currentExpanded = this.expanded();
      for (const id of toLoad) {
        const children = this.branches().get(id)?.children ?? [];
        for (const childId of children) {
          if (currentExpanded.has(childId)) {
            next.push(childId);
          }
        }
      }
      levelIds = next;
    }
  }

  private async loadBranch(
    id: string,
    options?: { append?: boolean },
  ): Promise<void> {
    const append = options?.append === true;
    const existing = this.branches().get(id);

    if (append) {
      if (!existing?.cursor || existing.status === 'loading') {
        return;
      }
    } else if (
      existing?.status === 'loading' ||
      existing?.status === 'loaded'
    ) {
      return;
    }

    this.setBranch(id, {
      status: 'loading',
      children: existing?.children ?? [],
      cursor: existing?.cursor ?? null,
    });

    try {
      const page = await this.companyApi.getCompanies(
        {
          companyId: id,
          sortBy: 'name',
          count: BRANCH_PAGE_SIZE,
        },
        append ? (existing?.cursor ?? undefined) : undefined,
      );
      const items = page.items ?? [];
      for (const item of items) {
        this.cacheNode(this.toNode(item));
      }

      const childIds = append
        ? [...(existing?.children ?? []), ...items.map((item) => item.id)]
        : items.map((item) => item.id);

      if (!append && items.length === 0) {
        this.knownLeaves.update((set) => {
          const next = new Set(set);
          next.add(id);
          return next;
        });
      }

      this.setBranch(id, {
        status: 'loaded',
        children: childIds,
        cursor: page.cursor || page.nextPageToken || null,
      });
    } catch (error) {
      console.error('Failed to load companies.', error);
      if (this.isForbiddenOrNotFound(error)) {
        const userCompanyId = this.userCompanyId();
        if (userCompanyId) {
          this.expansion.prune(userCompanyId, id);
        }
      }
      this.setBranchError(id, error);
      throw error;
    }
  }

  private setBranchError(id: string, error: unknown): void {
    const existing = this.branches().get(id);
    this.setBranch(id, {
      status: 'error',
      children: existing?.children ?? [],
      cursor: existing?.cursor ?? null,
      errorMessage: this.listErrorHandler.processErrorCode(
        'Companies',
        'load',
        error,
      ),
    });
  }

  private projectRows(): CompanyTreeRow[] {
    const rootId = this.userCompanyId();
    if (!rootId || !this.nodes().get(rootId)) {
      return [];
    }

    const rows: CompanyTreeRow[] = [];
    this.visitNode(rootId, 1, 1, 1, rows);
    return rows;
  }

  private visitNode(
    id: string,
    level: number,
    posInSet: number,
    setSize: number,
    rows: CompanyTreeRow[],
  ): void {
    const node = this.nodes().get(id);
    if (!node) {
      return;
    }

    const expanded = this.expanded().has(id);
    const branch = this.branches().get(id);
    const selectedId = this.selectedCompanyId();
    const selectedPath = this.selectedPath();

    rows.push({
      id: node.id,
      name: node.name,
      address: node.address ?? NO_ADDRESS_ON_RECORD,
      level,
      posInSet,
      setSize,
      variant: level === 1 ? 'root' : 'branch',
      expandable: !this.knownLeaves().has(id),
      expanded,
      selected: node.id === selectedId,
      containsSelected: selectedPath.includes(node.id) && !expanded,
      loading: branch?.status === 'loading',
      errorMessage: branch?.errorMessage ?? null,
      hasMore: expanded && !!branch?.cursor,
      href: '',
    });

    if (!expanded) {
      return;
    }

    const children = branch?.children ?? [];
    children.forEach((childId, index) => {
      this.visitNode(childId, level + 1, index + 1, children.length, rows);
    });
  }

  private toNode(company: CompanyLike, isRoot = false): CompanyNode {
    const formatted = this.addressUtils.formatShortAddress(company);
    return {
      id: company.id ?? '',
      name: company.name ?? '',
      parentId: isRoot ? null : (company.parentId ?? null),
      address: formatted === NO_ADDRESS_ON_RECORD ? null : formatted,
    };
  }

  private cacheNode(node: CompanyNode): void {
    if (!node.id) {
      return;
    }

    this.nodes.update((map) => {
      const next = new Map(map);
      next.set(node.id, node);
      return next;
    });
  }

  private setBranch(id: string, state: BranchState): void {
    this.branches.update((map) => {
      const next = new Map(map);
      next.set(id, state);
      return next;
    });
  }

  private markExpanded(id: string): void {
    const next = new Set(this.expanded());
    next.delete(id);
    next.add(id);
    this.expanded.set(next);
  }

  private persistExpanded(): void {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId) {
      return;
    }

    this.expansion.write(userCompanyId, [...this.expanded()]);
  }

  private ancestorsOf(id: string): string[] {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId || id === userCompanyId) {
      return [];
    }

    const path: string[] = [];
    let current = this.nodes().get(id);
    while (current?.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      path.unshift(parentId);
      current = this.nodes().get(parentId);
      if (!current) {
        break;
      }
    }

    if (!path.includes(userCompanyId)) {
      path.unshift(userCompanyId);
    }

    return path;
  }

  private isForbiddenOrNotFound(error: unknown): boolean {
    const status = this.httpStatus(error);
    return status === 403 || status === 404;
  }

  private httpStatus(error: unknown): number | undefined {
    const extracted = getError(error);
    if (typeof extracted?.status === 'number') {
      return extracted.status;
    }

    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: unknown }).status;
      return typeof status === 'number' ? status : undefined;
    }

    return undefined;
  }
}
